import type {UserState} from '@jobseeker/common-core/types';
import type {ProfileType} from '@jobseeker/common-core/services/types';

const EMAIL_SETTINGS_KEYS = [
  'jobFeed',
  'boost',
  'jobAction',
  'promotions',
  'education',
  'jobApply',
  'followUp',
  'testPending',
  'chat',
  'search',
  'unpublish',
] as const;

const PN_SETTINGS_KEYS = [
  'jobFeed',
  'boost',
  'jobAction',
  'promotions',
  'education',
  'chat',
  'search',
  'unpublish',
] as const;

const ACCOUNT_SETTINGS_KEYS = [
  'hideResume',
  'coverLetter',
  'similarCourseEnquiry',
  'hideStories',
  'invalidEmail',
  'twoFactorAuth',
  'emailChangeRequestedOn',
] as const;

const pickDefined = <T extends object>(
  source: T,
  keys: readonly (keyof T)[]
): Partial<T> =>
  keys.reduce((acc, key) => {
    if (source?.[key] !== undefined) acc[key] = source[key];
    return acc;
  }, {} as Partial<T>);

const getGender = (type: number) => {
  if (type === 1) {
    return 'male';
  } else if (type === 2) {
    return 'female';
  } else {
    return '';
  }
};

const getProfileCompleteness = (completeness: string) => {
  const parsedCompleteness = JSON.parse(completeness);

  return {
    personalDetails: parseInt(parsedCompleteness?.pd) ? true : false,
    professionalDetails: parseInt(parsedCompleteness?.prd) ? true : false,
    educationDetails: parseInt(parsedCompleteness?.ed) ? true : false,
    resumeAttached: parseInt(parsedCompleteness?.ra) ? true : false,
    followKeywords: parseInt(parsedCompleteness?.fk) ? true : false,
    skills: parseInt(parsedCompleteness?.skills) ? true : false,
    recommendations: parseInt(parsedCompleteness?.re['1']) ? true : false,
    profilePicture: parseInt(parsedCompleteness?.pp) ? true : false,
    profileVideoAudio: parseInt(parsedCompleteness?.rz) ? true : false,
    connectionsLinkedIn: parseInt(parsedCompleteness?.sm?.l) ? true : false,
    connectionsFacebook: parseInt(parsedCompleteness?.sm?.f) ? true : false,
    connectionsTwitter: parseInt(parsedCompleteness?.sm?.t) ? true : false,
    verificationEmail: parseInt(parsedCompleteness?.ev) ? true : false,
    verificationPhone: parseInt(parsedCompleteness?.pv) ? true : false,
  };
};

const getFormatedProfile = (profile: ProfileType): UserState => {
  const userProfile = {
    ...profile,
    id: profile?.id || 0,
    name: profile?.name || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    dob: profile?.dob || '',
    countryCode: profile?.countryCode || 0,
    country: profile?.countryCodeAlpha || '',
    gender: getGender(profile?.gender),
    experienceMonth: profile?.experienceMonth || 0,
    experienceYear: profile?.experienceYear || 0,
    locations: profile?.locations || {current: {}, preferred: []},
    functionalArea: profile?.functionalArea || {},
    currentCTC: profile?.currentCTC || '',
    currentSalary: profile?.currentSalary || 0,
    resumeUpdatedAt: profile?.resumeUpdatedAt || '',
    expectedCTC: profile?.expectedCTC || '',
    expectedSalary: profile?.expectedSalary || 0,
    picUrl: profile?.profilePicPath || '',
    resumePath: profile?.resumePath || '',
    maritalStatus: profile?.maritalStatus,
    audio: profile?.audio,
    video: profile?.video,
    audioPath: profile?.audioPath || '',
    videoPath: profile?.videoPath || '',
    experienceStatus: profile?.experienceStatus || 0,
    showSpotLight: profile?.subscriptions?.spotLight?.isValid || false,
    tags: profile?.tags || [{}],
    proMembership: profile?.proMembership || {},
    subscriptions: profile?.subscriptions,
    institute: profile?.institute || {},
    industry: profile?.industry || {},
    confidential: profile?.confidential || 0,
    negotiable: profile?.negotiable || 0,
    noticePeriod: profile?.noticePeriod || '',
    followUpsRemaining: profile?.followUpsRemaining || 0,
    coverText: profile?.coverText || '',
    coverLetterId: profile?.coverLetterId || 0,
    professionalInfo: profile?.professionalInfo,
    resumeBuilderProfileCompleteness:
      profile?.resumeBuilderProfileCompleteness || 0,
    educationInfo: profile?.educationInfo || {
      info: [
        {
          courseType: 0,
          degree: '',
        },
      ],
    },
    settings: {
      emailSettings: pickDefined(
        profile?.profileSettings ?? {},
        EMAIL_SETTINGS_KEYS
      ),
      pnSettings: {},
      accountSettings: pickDefined(
        profile?.profileSettings ?? {},
        ACCOUNT_SETTINGS_KEYS
      ),
    },
    resumeUpdated: profile?.resumeUpdated,
    isShortlisted: profile?.isShortlisted,
    showMaven: profile?.showMaven,
    competitionIdentity: profile?.competition?.identity || '',
    testSlug: profile?.competition?.testSlug || '',
    verifiedEmail: profile?.verifiedEmail || 0,
    verifiedNumber: profile?.verifiedNumber || 0,
    currentDesignation: profile?.currentDesignation || '',
  };

  const completeness = {
    percentage: profile?.completenessPercentage,
    profileStatus: getProfileCompleteness(profile?.completenessSerialize),
  };

  return {
    profileLoading: null as boolean | null,
    profile: userProfile,
    completeness: completeness,
  };
};

export {getFormatedProfile};
